import React, { useEffect, useState } from "react";
import api from "../../../api/api";
import toast from "react-hot-toast";
import { Download, Handshake, Save, Trash2 } from "lucide-react";

const statusColors = {
  new: "bg-blue-50 text-blue-700",
  reviewing: "bg-yellow-50 text-yellow-700",
  contacted: "bg-purple-50 text-purple-700",
  approved: "bg-green-50 text-green-700",
  declined: "bg-red-50 text-red-700",
  closed: "bg-slate-100 text-slate-700",
};

const AdminPartnerships = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    status: "new",
    admin_note: "",
  });

  const loadItems = async () => {
    try {
      const res = await api.get("/admin/partnerships");
      setItems(res.data || []);
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to load partnerships");
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const startEdit = (item) => {
    setEditing(item);
    setForm({
      status: item.status || "new",
      admin_note: item.admin_note || "",
    });
  };

  const updatePartnership = async (e) => {
    e.preventDefault();

    try {
      await api.put(`/admin/partnerships/${editing.id}`, form);
      toast.success("Partnership updated");
      setEditing(null);
      loadItems();
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to update partnership");
    }
  };

  const deletePartnership = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this partnership request?"
    );

    if (!confirmDelete) return;

    try {
      await api.delete(`/admin/partnerships/${id}`);
      toast.success("Partnership request deleted");
      loadItems();
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to delete request");
    }
  };

  const exportPartnerships = async () => {
    try {
      const res = await api.get("/admin/partnerships-export", {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "partnership-requests.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Partnership requests exported");
    } catch (err) {
      toast.error(err.cleanMessage || "Export failed");
    }
  };

  return (
    <div>
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-5 mb-8">
        <div>
          <h1 className="text-3xl font-black text-[#0b6b1d]">
            Partnership Requests
          </h1>
          <p className="text-slate-500 mt-1">
            Manage education partnership proposals and internal notes.
          </p>
        </div>

        <button
          onClick={exportPartnerships}
          className="px-5 py-3 rounded-2xl bg-[#0b6b1d] hover:bg-[#ff6600] text-white font-black flex items-center gap-2"
        >
          <Download size={18} />
          Export CSV
        </button>
      </div>

      <div className="grid xl:grid-cols-[1fr_420px] gap-6">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
          {items.length === 0 ? (
            <p className="p-6 text-slate-500">
              No partnership request submitted yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-black text-slate-600">
                      Organization
                    </th>
                    <th className="text-left p-4 text-sm font-black text-slate-600">
                      Contact
                    </th>
                    <th className="text-left p-4 text-sm font-black text-slate-600">
                      Type
                    </th>
                    <th className="text-left p-4 text-sm font-black text-slate-600">
                      Status
                    </th>
                    <th className="text-right p-4 text-sm font-black text-slate-600">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-b last:border-b-0">
                      <td className="p-4">
                        <p className="font-black text-slate-900">
                          {item.organization_name}
                        </p>
                        <p className="text-sm text-slate-600 line-clamp-2 mt-1">
                          {item.message}
                        </p>
                      </td>

                      <td className="p-4">
                        <p className="font-bold text-slate-800">
                          {item.contact_person}
                        </p>
                        <p className="text-xs text-slate-500">{item.email}</p>
                        <p className="text-xs text-slate-500">{item.phone}</p>
                      </td>

                      <td className="p-4 text-sm text-slate-600">
                        {item.partnership_type}
                      </td>

                      <td className="p-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-black uppercase ${
                            statusColors[item.status] || "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>

                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => startEdit(item)}
                            className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-[#0b6b1d] hover:text-white flex items-center justify-center"
                          >
                            <Handshake size={17} />
                          </button>

                          <button
                            onClick={() => deletePartnership(item.id)}
                            className="w-10 h-10 rounded-xl bg-red-50 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center"
                          >
                            <Trash2 size={17} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <form
          onSubmit={updatePartnership}
          className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm h-fit"
        >
          <h2 className="text-xl font-black text-[#0b6b1d] mb-5">
            {editing ? "Update Request" : "Select a Request"}
          </h2>

          {editing ? (
            <>
              <div className="mb-5 p-4 rounded-2xl bg-slate-50 border border-slate-100">
                <p className="font-black text-slate-900">
                  {editing.organization_name}
                </p>
                <p className="text-sm text-slate-500">
                  {editing.partnership_type}
                </p>
              </div>

              <label className="block mb-5">
                <span className="font-bold text-slate-700">Status</span>

                <select
                  value={form.status}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, status: e.target.value }))
                  }
                  className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
                >
                  <option value="new">New</option>
                  <option value="reviewing">Reviewing</option>
                  <option value="contacted">Contacted</option>
                  <option value="approved">Approved</option>
                  <option value="declined">Declined</option>
                  <option value="closed">Closed</option>
                </select>
              </label>

              <label className="block mb-6">
                <span className="font-bold text-slate-700">
                  Internal Admin Note
                </span>

                <textarea
                  rows="7"
                  value={form.admin_note}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      admin_note: e.target.value,
                    }))
                  }
                  className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
                  placeholder="Write internal note about this partnership request."
                />
              </label>

              <button className="w-full py-4 rounded-2xl bg-[#0b6b1d] hover:bg-[#ff6600] text-white font-black flex items-center justify-center gap-2">
                <Save size={18} />
                Save Update
              </button>

              <button
                type="button"
                onClick={() => setEditing(null)}
                className="w-full mt-3 py-3 rounded-2xl bg-slate-100 text-slate-700 font-black"
              >
                Cancel
              </button>
            </>
          ) : (
            <p className="text-slate-500 leading-relaxed">
              Click the handshake icon beside any request to update its status or add an internal note.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default AdminPartnerships;